import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
  faHistory,
  faFileInvoiceDollar,
  faLockOpen,
  faArrowAltCircleRight
} from '@fortawesome/free-solid-svg-icons';

import { useAuthUser } from '../../hooks/useAuthUser';
import { IAppState } from '../../interfaces';
import * as actions from '../../store/actions';
import OfferService from '../../services/OfferService';
import { batchGetOfferRequests } from '../../store/actions/offerRequestActions';
import { IOffer, IOfferRequest } from '../../interfaces/domain';
import { Routes } from '../../classes/Routes';

import Spinner from '../../components/UI/Spinner/Spinner';
import Alert from '../../components/UI/Alert/Alert';
import classes from './OfferHistory.module.scss';

interface IProps extends RouteComponentProps {}

type TActiveTab = 'offers' | 'leads';
type TOfferStatusFilter = 'ALL' | 'SENT' | 'WON' | 'DONE_ARCHIVED' | 'DRAFT' | 'LOST';
type TSortField = 'date' | 'client' | 'service' | 'price' | 'status' | 'billing';
type TSortDirection = 'asc' | 'desc';

const OfferHistory: React.FC<IProps> = ({ history }) => {
  const dispatch = useDispatch();
  const authUser = useAuthUser();

  const isMicro = authUser?.isMicro || false;

  // Tabs state
  const [activeTab, setActiveTab] = useState<TActiveTab>('offers');
  const [offerStatusFilter, setOfferStatusFilter] = useState<TOfferStatusFilter>('ALL');

  // Search and Sort state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortField, setSortField] = useState<TSortField>('date');
  const [sortDirection, setSortDirection] = useState<TSortDirection>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  // Redux data
  const { offerRequests, offerRequestAccess, loadingAccess } = useSelector((state: IAppState) => ({
    offerRequests: state.offerRequest.offerRequests,
    offerRequestAccess: state.offerRequest.offerRequestAccess,
    loadingAccess: state.offerRequest.loading
  }));

  // Local state for offers and offer requests
  const [offers, setOffers] = useState<IOffer[]>([]);
  const [localOfferRequests, setLocalOfferRequests] = useState<IOfferRequest[]>([]);
  const [loadingOffers, setLoadingOffers] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoadingOffers(true);
      setError(null);

      // Load offer requests (Redux)
      dispatch(actions.listOfferRequests({ limit: true }));

      // Load offers (Direct Service Call)
      const providerId = authUser?.isServiceProvider && authUser?.organization?.id
        ? authUser.organization.id.toString()
        : undefined;
      const fetchedOffers = await OfferService.getOffers(undefined, providerId);
      setOffers(fetchedOffers || []);

      // If MICRO user, load purchased leads
      if (isMicro && authUser?.organization?.id) {
        dispatch(actions.listOfferRequestAccess({ organizationId: authUser.organization.id.toString() }));
      }
    } catch (err) {
      console.error('Error loading history data: ', err);
      setError('Tietojen lataaminen palvelimelta epäonnistui.');
    } finally {
      setLoadingOffers(false);
    }
  }, [dispatch, isMicro, authUser?.organization?.id, authUser?.isServiceProvider]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep track of IDs we have already fetched or are currently fetching to prevent infinite/duplicate request loops
  const fetchedOrLoadingIdsRef = useRef<Set<string>>(new Set());

  // Sync local offer requests list with Redux list by MERGING (never wipe out locally fetched ones)
  useEffect(() => {
    if (Array.isArray(offerRequests)) {
      offerRequests.forEach(req => {
        if (req && req.id) {
          fetchedOrLoadingIdsRef.current.add(req.id.toString());
        }
      });

      setLocalOfferRequests(prev => {
        const merged = [...prev];
        offerRequests.forEach(req => {
          if (req && req.id && !merged.some(r => r.id?.toString() === req.id.toString())) {
            merged.push(req);
          }
        });
        return merged;
      });
    }
  }, [offerRequests]);


  // Reset page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, offerStatusFilter, searchQuery, sortField, sortDirection, pageSize]);

  // Date Formatting Helpers (consistent with CRM & CRM list)
  const formatFullDateTime = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} klo ${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  const formatRelativeTime = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHr = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHr / 24);

      if (diffSec < 60) {
        return 'juuri nyt';
      } else if (diffMin < 60) {
        return `${diffMin} min sitten`;
      } else if (diffHr < 24) {
        return `${diffHr} t sitten`;
      } else if (diffDays === 1) {
        return 'eilen';
      } else {
        return `${diffDays} pv sitten`;
      }
    } catch (e) {
      return '';
    }
  };

  // Sort Handler
  const handleSort = (field: TSortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Helper to render Sort Icons
  const renderSortIcon = (field: TSortField) => {
    if (sortField !== field) {
      return <FontAwesomeIcon icon={faSort} className={classes.SortIcon} />;
    }
    return sortDirection === 'asc' ? (
      <FontAwesomeIcon icon={faSortUp} className={classes.SortIcon} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} className={classes.SortIcon} />
    );
  };

  // 1. FILTERED & SORTED OFFERS (Tab 1)
  const historicalOffers = useMemo(() => {
    const matched = offers
      .filter(o => {
        if (o.deleted) return false;
        if (offerStatusFilter === 'ALL') return true;
        if (offerStatusFilter === 'SENT') return o.state === 'SENT' && !o.done && !o.archived;
        if (offerStatusFilter === 'WON') return o.state === 'WON' && !o.done && !o.archived;
        if (offerStatusFilter === 'DONE_ARCHIVED') return o.done || o.archived;
        if (offerStatusFilter === 'DRAFT') return o.state === 'DRAFT' && !o.done && !o.archived;
        if (offerStatusFilter === 'LOST') return o.state === 'LOST' && !o.done && !o.archived;
        return true;
      })
      .map(o => {
        const request = Array.isArray(localOfferRequests) ? localOfferRequests.find(r => r.id?.toString() === o.offerRequestId?.toString()) : undefined;
        return {
          offer: o,
          request
        };
      });

    // Search filter
    const filtered = matched.filter(item => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase().trim();
      const clientName = item.request
        ? `${item.request.firstName || ''} ${item.request.lastName || ''}`.toLowerCase()
        : '';
      const city = item.request?.city?.toLowerCase() || '';
      const address = item.request?.address?.toLowerCase() || '';
      const serviceName = item.request?.service?.name?.toLowerCase() || '';
      const offerId = item.offer.id?.toString() || '';
      const reqId = item.offer.offerRequestId?.toString() || '';
      const spName = item.offer.serviceProviderName?.toLowerCase() || '';
      const message = item.offer.message?.toLowerCase() || '';
      const notes = item.offer.notes?.toLowerCase() || '';
      const priceStr = item.offer.priceWithVat ? item.offer.priceWithVat.toString() : '';

      return clientName.includes(query) ||
             city.includes(query) ||
             address.includes(query) ||
             serviceName.includes(query) ||
             spName.includes(query) ||
             offerId.includes(query) ||
             reqId.includes(query) ||
             message.includes(query) ||
             notes.includes(query) ||
             priceStr.includes(query);
    });

    // Sorting logic
    return filtered.sort((a, b) => {
      let comparison = 0;

      if (sortField === 'date') {
        const dateA = new Date(a.offer.updated || a.offer.created || 0).getTime();
        const dateB = new Date(b.offer.updated || b.offer.created || 0).getTime();
        comparison = dateA - dateB;
      } else if (sortField === 'client') {
        const nameA = a.request?.lastName || '';
        const nameB = b.request?.lastName || '';
        comparison = nameA.localeCompare(nameB);
      } else if (sortField === 'service') {
        const sA = a.request?.service?.name || '';
        const sB = b.request?.service?.name || '';
        comparison = sA.localeCompare(sB);
      } else if (sortField === 'price') {
        const pA = a.offer.priceWithVat || 0;
        const pB = b.offer.priceWithVat || 0;
        comparison = pA - pB;
      } else if (sortField === 'status') {
        const stateA = a.offer.done ? 'DONE' : (a.offer.state || '');
        const stateB = b.offer.done ? 'DONE' : (b.offer.state || '');
        comparison = stateA.localeCompare(stateB);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [offers, localOfferRequests, offerStatusFilter, searchQuery, sortField, sortDirection]);

  // PAGINATED OFFERS
  const paginatedOffers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return historicalOffers.slice(startIndex, startIndex + pageSize);
  }, [historicalOffers, currentPage, pageSize]);

  // Batch fetch missing offer requests for all offers (fast single request instead of N individual GETs)
  useEffect(() => {
    if (offers.length > 0) {
      const missingIds = offers
        .map(item => item.offerRequestId?.toString())
        .filter((id): id is string => {
          if (!id) return false;
          const alreadyTracked = fetchedOrLoadingIdsRef.current.has(id) || localOfferRequests.some(r => r.id?.toString() === id);
          return !alreadyTracked;
        });

      if (missingIds.length > 0) {
        const uniqueMissingIds = Array.from(new Set(missingIds));
        
        uniqueMissingIds.forEach(id => {
          fetchedOrLoadingIdsRef.current.add(id);
        });

        batchGetOfferRequests(uniqueMissingIds)
          .then(results => {
            if (results && results.length > 0) {
              setLocalOfferRequests(prev => {
                const merged = [...prev];
                results.forEach(req => {
                  if (req && req.id && !merged.some(r => r.id?.toString() === req.id?.toString())) {
                    merged.push(req);
                  }
                });
                return merged;
              });
            }
          })
          .catch(err => {
            console.warn("Batch fetch missing offer requests failed in OfferHistory:", err);
            uniqueMissingIds.forEach(id => fetchedOrLoadingIdsRef.current.delete(id));
          });
      }
    }
  }, [offers, localOfferRequests]);

  // 2. FILTERED & SORTED PURCHASED LEADS (Tab 2 - MICRO only)
  const purchasedLeads = useMemo(() => {
    if (!Array.isArray(offerRequestAccess)) return [];

    // Search filter
    const filtered = offerRequestAccess.filter(access => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const name = access.offerRequestUserName?.toLowerCase() || '';
      const service = access.serviceName?.toLowerCase() || '';
      return name.includes(query) || service.includes(query);
    });

    // Sorting logic
    return [...filtered].sort((a, b) => {
      let comparison = 0;

      if (sortField === 'date') {
        const dateA = new Date(a.offerRequestCreated || a.created || 0).getTime();
        const dateB = new Date(b.offerRequestCreated || b.created || 0).getTime();
        comparison = dateA - dateB;
      } else if (sortField === 'client') {
        const nameA = a.offerRequestUserName || '';
        const nameB = b.offerRequestUserName || '';
        comparison = nameA.localeCompare(nameB);
      } else if (sortField === 'service') {
        const sA = a.serviceName || '';
        const sB = b.serviceName || '';
        comparison = sA.localeCompare(sB);
      } else if (sortField === 'billing') {
        const bA = a.paymentStatus || '';
        const bB = b.paymentStatus || '';
        comparison = bA.localeCompare(bB);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [offerRequestAccess, searchQuery, sortField, sortDirection]);

  // PAGINATED PURCHASED LEADS
  const paginatedLeads = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return purchasedLeads.slice(startIndex, startIndex + pageSize);
  }, [purchasedLeads, currentPage, pageSize]);

  // 3. UPCOMING BILLING COMPUTATIONS (MICRO only)
  const billingSummary = useMemo(() => {
    if (!isMicro || !Array.isArray(offerRequestAccess)) {
      return { count: 0, netSum: 0, grossSum: 0 };
    }

    const pendingLeads = offerRequestAccess.filter(access => access.paymentStatus === 'PENDING');
    const count = pendingLeads.length;
    const netSum = count * 19.00;
    const grossSum = netSum * 1.255; // 25.5% standard VAT rate

    return {
      count,
      netSum,
      grossSum
    };
  }, [isMicro, offerRequestAccess]);

  const handleRowClick = (offerRequestId?: number | string) => {
    if (offerRequestId) {
      history.push(Routes.OFFER_REQUEST(offerRequestId.toString()));
    }
  };

  // Reusable Premium Pagination Component
  const renderPagination = (totalItems: number) => {
    if (totalItems <= 20 && pageSize === 20) return null;

    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    // Generate page buttons array
    const pages = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className={classes.PaginationRow}>
        <div className={classes.PageSizeSelector}>
          <span>Rivejä sivulla:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className={classes.PaginationInfo}>
          Näytetään {startItem}-{endItem} / {totalItems} kpl
        </div>

        <div className={classes.PaginationControls}>
          <button
            className={classes.PaginationBtn}
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            &lt;
          </button>
          
          {startPage > 1 && (
            <>
              <button
                className={`${classes.PaginationBtn} ${currentPage === 1 ? classes.ActiveBtn : ''}`}
                onClick={() => setCurrentPage(1)}
              >
                1
              </button>
              {startPage > 2 && <span style={{ padding: '0 0.25rem', color: '#94a3b8' }}>...</span>}
            </>
          )}

          {pages.map(page => (
             <button
               key={page}
               className={`${classes.PaginationBtn} ${currentPage === page ? classes.ActiveBtn : ''}`}
               onClick={() => setCurrentPage(page)}
             >
               {page}
             </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span style={{ padding: '0 0.25rem', color: '#94a3b8' }}>...</span>}
              <button
                className={`${classes.PaginationBtn} ${currentPage === totalPages ? classes.ActiveBtn : ''}`}
                onClick={() => setCurrentPage(totalPages)}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            className={classes.PaginationBtn}
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            &gt;
          </button>
        </div>
      </div>
    );
  };

  const renderOffersTable = () => {
    if (historicalOffers.length === 0) {
      return (
        <div className={classes.EmptyMessage}>
          <FontAwesomeIcon icon={faHistory} />
          <p>{offers.length === 0 ? 'Ei tehtyjä tarjouksia.' : 'Ei valitulla suodatuksella tai hakusanalla löytyneitä tarjouksia.'}</p>
        </div>
      );
    }

    return (
      <>
        <div className={classes.TableContainer}>
          <table className={classes.Table}>
            <thead>
              <tr>
                <th onClick={() => handleSort('date')}>Päivämäärä {renderSortIcon('date')}</th>
                <th onClick={() => handleSort('client')}>Asiakas {renderSortIcon('client')}</th>
                <th onClick={() => handleSort('service')}>Palvelu {renderSortIcon('service')}</th>
                <th onClick={() => handleSort('price')}>Tarjoushinta {renderSortIcon('price')}</th>
                <th onClick={() => handleSort('status')}>Tila {renderSortIcon('status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginatedOffers.map(({ offer, request }) => {
                const isArchived = offer.archived;
                const dateToFormat = offer.updated || offer.created;
                
                // Resolve visual state labels
                let statusLabel = 'Aktiivinen';
                let badgeColor = classes.Amber;
                if (offer.done) {
                  statusLabel = 'Valmis';
                  badgeColor = classes.Green;
                } else if (offer.state === 'WON') {
                  statusLabel = 'Voitettu';
                  badgeColor = classes.Green;
                } else if (offer.state === 'SENT') {
                  statusLabel = 'Lähetetty';
                  badgeColor = classes.Amber;
                } else if (offer.state === 'DRAFT') {
                  statusLabel = 'Luonnos';
                  badgeColor = classes.Gray;
                } else if (offer.state === 'LOST') {
                  statusLabel = 'Ei tilannut';
                  badgeColor = classes.Red;
                } else if (isArchived) {
                  statusLabel = 'Arkistoitu';
                  badgeColor = classes.Gray;
                }

                return (
                  <tr
                    key={offer.id}
                    onClick={() => handleRowClick(offer.offerRequestId)}
                    className={isArchived ? classes.MutedRow : ''}
                  >
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                          {dateToFormat && formatFullDateTime(dateToFormat)}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {dateToFormat && formatRelativeTime(dateToFormat)}
                        </span>
                      </div>
                    </td>
                    <td>
                      {request ? (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: '#0f172a' }}>
                            {request.lastName ? `${request.lastName}, ${request.firstName || ''}` : (request.firstName || '')}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {request.zip} {request.city}
                          </span>
                        </div>
                      ) : (
                        <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>Ladataan asiakasta...</span>
                      )}
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{request?.service?.name || ''}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>
                          {offer.priceWithVat ? `${offer.priceWithVat.toFixed(2)} €` : '0.00 €'}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {offer.priceWithoutVat ? `${offer.priceWithoutVat.toFixed(2)} € alv 0%` : '0.00 € alv 0%'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`${classes.Badge} ${badgeColor}`}>{statusLabel}</span>
                    </td>
                    <td className={classes.ArrowCell}>
                      <FontAwesomeIcon icon={faArrowAltCircleRight} style={{ fontSize: '1.1rem', color: '#3b82f6' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination(historicalOffers.length)}
      </>
    );
  };

  const renderLeadsTable = () => {
    if (purchasedLeads.length === 0) {
      return (
        <div className={classes.EmptyMessage}>
          <FontAwesomeIcon icon={faLockOpen} />
          <p>Ei ostettuja tarjouspyyntöjä.</p>
        </div>
      );
    }

    return (
      <>
        <div className={classes.TableContainer}>
          <table className={classes.Table}>
            <thead>
              <tr>
                <th onClick={() => handleSort('date')}>Ostopäivä {renderSortIcon('date')}</th>
                <th onClick={() => handleSort('client')}>Asiakas & Sijainti {renderSortIcon('client')}</th>
                <th onClick={() => handleSort('service')}>Palvelu {renderSortIcon('service')}</th>
                <th onClick={() => handleSort('billing')}>Laskutustila {renderSortIcon('billing')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.map(access => {
                const isPending = access.paymentStatus === 'PENDING';
                const dateToFormat = access.offerRequestCreated || access.created;

                return (
                  <tr key={access.id} onClick={() => handleRowClick(access.offerRequestId)}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                          {dateToFormat && formatFullDateTime(dateToFormat)}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {dateToFormat && formatRelativeTime(dateToFormat)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, color: '#0f172a' }}>{access.offerRequestUserName}</span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{access.serviceName}</span>
                    </td>
                    <td>
                      {isPending ? (
                        <span className={`${classes.Badge} ${classes.Amber}`}>
                          Laskuttamatta (19 € + alv)
                        </span>
                      ) : (
                        <span className={`${classes.Badge} ${classes.Green}`}>
                          Laskutettu {access.invoiceId ? `(Lasku #${access.invoiceId})` : ''}
                        </span>
                      )}
                    </td>
                    <td className={classes.ArrowCell}>
                      <FontAwesomeIcon icon={faArrowAltCircleRight} style={{ fontSize: '1.1rem', color: '#3b82f6' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination(purchasedLeads.length)}
      </>
    );
  };

  const isDataLoading = loadingOffers || (isMicro && loadingAccess);

  return (
    <div className={classes.Container}>
      <div className={classes.Header}>
        <div>
          <h1>Tarjoushistoria</h1>
          <p className={classes.Subtitle}>
            Tarkastele kaikkia tehtyjä tarjouksia, arkistoituja myyntejä ja ostamiasi tarjouspyyntöjä.
          </p>
        </div>
      </div>

      {isMicro && (
        <div className={classes.TabsContainer}>
          <button
            className={`${classes.TabButton} ${activeTab === 'offers' ? classes.ActiveTab : ''}`}
            onClick={() => {
              setActiveTab('offers');
              setSortField('date');
            }}
          >
            Tarjoukset ({historicalOffers.length})
          </button>
          <button
            className={`${classes.TabButton} ${activeTab === 'leads' ? classes.ActiveTab : ''}`}
            onClick={() => {
              setActiveTab('leads');
              setSortField('date');
            }}
          >
            Ostetut liidit ({purchasedLeads.length})
          </button>
        </div>
      )}

      {error && <Alert children={error} />}

      {isMicro && activeTab === 'leads' && !isDataLoading && (
        <div className={classes.BillingCard}>
          <div className={classes.BillingLeft}>
            <div className={classes.BillingIcon}>
              <FontAwesomeIcon icon={faFileInvoiceDollar} />
            </div>
            <div className={classes.BillingTitle}>
              <h3>Tuleva laskutus</h3>
              <p>Laskuttamatta olevien mikromaksullisten avausten yhteenveto tältä kuulta.</p>
            </div>
          </div>
          <div className={classes.BillingRight}>
            <div className={classes.BillingMetric}>
              <span className={classes.MetricLabel}>Avaamiset</span>
              <span className={classes.MetricValue}>{billingSummary.count} kpl</span>
            </div>
            <div className={classes.BillingMetric}>
              <span className={classes.MetricLabel}>Veroton Summa</span>
              <span className={classes.MetricValue}>{billingSummary.netSum.toFixed(2)} €</span>
              <span className={classes.MetricSubValue}>alv 0%</span>
            </div>
            <div className={classes.BillingMetric}>
              <span className={classes.MetricLabel}>Verollinen Summa</span>
              <span className={classes.MetricValue}>{billingSummary.grossSum.toFixed(2)} €</span>
              <span className={classes.MetricSubValue}>sis. alv 25,5%</span>
            </div>
          </div>
        </div>
      )}

      <div className={classes.SearchContainer}>
        <div className={classes.SearchBox}>
          <FontAwesomeIcon icon={faSearch} className={classes.SearchIcon} />
          <input
            type="text"
            placeholder={
              activeTab === 'offers'
                ? 'Hae asiakkaan, sijainnin tai palvelun nimellä...'
                : 'Hae ostettua liidiä nimellä tai palvelulla...'
            }
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {activeTab === 'offers' && (
        <div className={classes.StatusFilterContainer}>
          <button
            type="button"
            className={`${classes.StatusFilterBtn} ${offerStatusFilter === 'ALL' ? classes.ActiveStatusFilter : ''}`}
            onClick={() => setOfferStatusFilter('ALL')}
          >
            Kaikki ({offers.filter(o => !o.deleted).length})
          </button>
          <button
            type="button"
            className={`${classes.StatusFilterBtn} ${offerStatusFilter === 'SENT' ? classes.ActiveStatusFilter : ''}`}
            onClick={() => setOfferStatusFilter('SENT')}
          >
            Lähetetyt ({offers.filter(o => !o.deleted && o.state === 'SENT' && !o.done && !o.archived).length})
          </button>
          <button
            type="button"
            className={`${classes.StatusFilterBtn} ${offerStatusFilter === 'WON' ? classes.ActiveStatusFilter : ''}`}
            onClick={() => setOfferStatusFilter('WON')}
          >
            Voitetut ({offers.filter(o => !o.deleted && o.state === 'WON' && !o.done && !o.archived).length})
          </button>
          <button
            type="button"
            className={`${classes.StatusFilterBtn} ${offerStatusFilter === 'DONE_ARCHIVED' ? classes.ActiveStatusFilter : ''}`}
            onClick={() => setOfferStatusFilter('DONE_ARCHIVED')}
          >
            Valmiit & Arkistoidut ({offers.filter(o => !o.deleted && (o.done || o.archived)).length})
          </button>
          <button
            type="button"
            className={`${classes.StatusFilterBtn} ${offerStatusFilter === 'DRAFT' ? classes.ActiveStatusFilter : ''}`}
            onClick={() => setOfferStatusFilter('DRAFT')}
          >
            Luonnokset ({offers.filter(o => !o.deleted && o.state === 'DRAFT' && !o.done && !o.archived).length})
          </button>
          <button
            type="button"
            className={`${classes.StatusFilterBtn} ${offerStatusFilter === 'LOST' ? classes.ActiveStatusFilter : ''}`}
            onClick={() => setOfferStatusFilter('LOST')}
          >
            Ei tilannut ({offers.filter(o => !o.deleted && o.state === 'LOST' && !o.done && !o.archived).length})
          </button>
        </div>
      )}

      {isDataLoading ? (
        <div style={{ textAlign: 'center', padding: '5rem 0' }}>
          <Spinner />
        </div>
      ) : activeTab === 'offers' ? (
        renderOffersTable()
      ) : (
        renderLeadsTable()
      )}
    </div>
  );
};

export default withRouter(OfferHistory);
